import {Key} from './key';

function createStore(entityFactory) {

	class Container {
		constructor() {
			this.children = {}; // Map<kind,<id,{table}>>
		}

		_getRecord(key) {
			let record = this, children;
			for (let segment of key.segments()) {
				children = record.children[segment.kind];
				if (children) {
					record = children.table[segment.id];
				} else {
					record = null;
				}
				if (!record) {
					break;
				}
			}
			return record;
		}

		_ensureChildren(kind) {
			let children = this.children[kind];
			if (!children) {
				children = {};
				this.children[kind] = children;
			}
			return children;

		}

		_ensureTable(kind) {
			let children = this._ensureChildren(kind)
			let table = children.table;
			if (!table) {
				table = {};
				children.table = table;
			}
			return table;
		}

		_putRecord(kind, rawEntity) {
			if (!rawEntity) throw "Container._putRecord: rawEntity param is invalid";

			let table = this._ensureTable(kind);

			let rawChildren = rawEntity.children;
			if (rawChildren) {
				delete rawEntity.children;
			}

			// entity is null if this is a Datastore
			let entity = entityFactory(this.entity || null, kind, rawEntity);
			let record = table[entity.id], oldEntity, trueIfCreate;
			if (record) {
				oldEntity = record.entity.clone();
				// Set to null properties that have evicted from the new entity
				let newKeys = new Set(Object.keys(entity));
				for (let p in record.entity) {
					if (record.entity.hasOwnProperty(p) && !newKeys.has(p)) {
						record.entity[p] = null;
					}
				}
				Object.assign(record.entity, entity);
				trueIfCreate = false;
			} else {
				trueIfCreate = true;
				record = new Record(entity);
				table[entity.id] = record;
				oldEntity = null;
			}

			// Create children
			if (rawChildren) {
				for (let kind in rawChildren) {
					for (let rawChild of rawChildren[kind]) {
						record._putRecord(kind, rawChild);
					}
				}
			}

			if ('postInit' in record.entity) {
				record.entity.postInit();
			}

			return [trueIfCreate, record, oldEntity];
		}
	}


	class Record extends Container {
		constructor(entity) {
			super();
			this.entity = entity;
		}
	}


	class Datastore extends Container {
		constructor() {
			super();
		}

		/**
		 * @param {Key} parentKey The key of the parent or the key of the entity if single argument
		 * @param {String} kind
		 * @param {Number} id
		 * @returns {Object}
		 */
		get(parentKey, kind, id) {
			let key;
			if (kind && id) {
				key = new Key(parentKey, kind, id);
			} else {
				key = parentKey;
			}
			let entity = null;
			if (key) {
				let record = this._getRecord(key);
				if (record) {
					entity = record.entity;
				}
			}
			return entity;
		}

		/**
		 * Signature:
		 * - put(parentKey, kind, rawEntities)
		 *    => rawEntities is an object or an array of objects that must have an id
		 * - put(rawEntity)
		 *    => rawEntity is an object that must have a key
		 */
		put(parentKey, kind, rawEntities) {
			let singleResult;

			if (kind && rawEntities) {
				singleResult = typeof rawEntities[Symbol.iterator] !== 'function';
				if (singleResult) {
					rawEntities = [rawEntities];
				}
			} else {
				singleResult = true;
				let rawEntity = parentKey;
				let key = new Key(rawEntity.key);
				parentKey = key.parent;
				kind = key.kind;
				rawEntities = [rawEntity];
			}
			let parentRecord = parentKey ? this._getRecord(parentKey) : this;

			let result;
			if (!parentRecord) {
				console.warn(`Cannot find parent key: ${parentKey.toString()}`);
				result = [];
			} else if (rawEntities) {
				parentRecord._ensureTable(kind);
				let entities = [], createdEntities = [], changedEntities = [],
					trueIfCreate, record, oldEntity;
				for (let rawEntity of rawEntities) {
					[trueIfCreate, record, oldEntity] = parentRecord._putRecord(kind, rawEntity);
					entities.push(record.entity);
					if (trueIfCreate) {
						createdEntities.push(record.entity);
					} else {
						changedEntities.push({
							oldEntity: oldEntity,
							newEntity: record.entity
						});
					}
				}
				if (createdEntities.length) {
					const event = {
						parent: parentRecord.entity,
						kind,
						entities: createdEntities
					};
					$(this).trigger(`${kind}.add`, event);
					$(parentRecord).trigger('wow.children.add', event);
				}
				if (changedEntities.length) {
					const event = {
						parent: parentRecord.entity,
						kind,
						entities: changedEntities
					};
					$(this).trigger(`${kind}.update`, event);
					$(parentRecord).trigger('wow.children.update', event);
				}
				result = entities;
			} else {
				result = [];
			}

			return singleResult && result ? result[0] : result;
		}

		delete(parentKey, kind, ids) {
			let entities = [];
			if (ids && ids.length > 0) {
				let parentRecord = parentKey ? this._getRecord(parentKey) : this;
				let children = parentRecord.children[kind];
				if (children) {
					const table = children.table;
					let record;
					for (let id of ids) {
						record = table[id];
						if (record) {
							entities.push(record.entity);
							delete table[id];
						}
					}

					const event = {
						parent: parentRecord.entity,
						kind,
						ids,
						entities
					};
					$(this).trigger(`${kind}.remove`, event);
					$(parentRecord).trigger('wow.children.remove', event);
				}
			}
			return entities;
		}

		_observeParams(parentKeyOrKind, eventType) {
			let eventSource;
			if (typeof parentKeyOrKind === 'string') {
				eventSource = this;
				eventType = parentKeyOrKind + '.' + eventType;
			} else {
				eventSource = this._getRecord(parentKeyOrKind);
			}
			return [eventSource, eventType];
		}

		observe(parentKeyOrKind, eventType, callback) {
			let eventSource;
			[eventSource, eventType] = this._observeParams(parentKeyOrKind, eventType);
			$(eventSource).on(eventType, null, null, callback);
		}

		unobserve(parentKeyOrKind, eventType, callback) {
			let eventSource;
			[eventSource, eventType] = this._observeParams(parentKeyOrKind, eventType);
			$(eventSource).off(eventType, callback);
		}

		/**
		 * @param {Key} parentKey
		 * @param {String} kind
		 * @param  {Array} ids. Optional
		 * @returns {Array}
		 */
		getByParent(parentKey, kind, ids) {
			let record = parentKey ? this._getRecord(parentKey) : this;
			let recordChildren = record.children[kind];
			let result;
			if (recordChildren) {
				const childrenTable = recordChildren.table;
				const childrenIds = ids || Object.keys(childrenTable);
				result = childrenIds.map(id => {
					return childrenTable[id].entity
				});
			} else {
				result = [];
			}
			return result;
		}

		/**
		 * Usage:
		 * - childrenAttr(parentKey, kind, attrName): returns the attribute of the children of the given kind
		 * - childrenAttr(parentKey, kind, attrName, attrValue): sets the attribute
		 *
		 * @param {Key} parentKey
		 * @param {string} kind
		 * @param {string} attrName
		 * @param attrValue
		 * @returns the attribute value
		 */
		childrenAttr(parentKey, kind, attrName, attrValue) {
			// Get attribute
			if (attrValue === undefined) {
				attrValue = null;
				let record = this._getRecord(parentKey);
				if (record) {
					let children = this._getRecord(parentKey).children[kind];
					if (children) {
						attrValue = children[attrName];
					}
				}
			}
			// Set attribute
			else {
				const record = this._getRecord(parentKey);
				const children = record._ensureChildren(kind);
				children[attrName] = attrValue;
			}
			return attrValue;
		}
	}

	return new Datastore();
}

export class Observer {
	constructor(eventPrefix, parentKeyOrKind, impl) {
		this.__eventPrefix = eventPrefix;
		this.__parentKeyOrKind = parentKeyOrKind;
		impl = impl || this;
		if (impl.onAdded) this._onAdded = impl.onAdded.bind(impl);
		if (impl.onRemoved) this._onRemoved = impl.onRemoved.bind(impl);
		if (impl.onUpdated) this._onUpdated = impl.onUpdated.bind(impl);
	}

	register() {
		if (this._onAdded) store.observe(this.__parentKeyOrKind, this.__eventPrefix + '.add', this._onAdded);
		if (this._onRemoved) store.observe(this.__parentKeyOrKind, this.__eventPrefix + '.remove', this._onRemoved);
		if (this._onUpdated) store.observe(this.__parentKeyOrKind, this.__eventPrefix + '.update', this._onUpdated);
	}

	unregister() {
		if (this._onAdded) store.unobserve(this.__parentKeyOrKind, this.__eventPrefix + '.add', this._onAdded);
		if (this._onRemoved) store.unobserve(this.__parentKeyOrKind, this.__eventPrefix + '.remove', this._onRemoved);
		if (this._onUpdated) store.unobserve(this.__parentKeyOrKind, this.__eventPrefix + '.update', this._onUpdated);
	}
}

export class ChildrenObserver extends Observer {
	constructor(parentKey, impl) {
		super('wow.children', parentKey, impl);
	}
}

export class StoreObserver extends Observer {
	constructor(kind, impl) {
		super('wow', kind, impl);
	}
}


let store;
const globals = {};

export default {
	init(entityFactory) {
		//FIXME: do not publish store to global, one day...
		window.__store__ = store = createStore(entityFactory);
		window.__globals__ = globals;
	},
	get(parentKey, kind, id) {
		return store.get(parentKey, kind, id);
	},
	getByParent(parentKey, kind, ids) {
		return store.getByParent(parentKey, kind, ids);
	},
	childrenAttr(parentKey, kind, attrName, attrValue) {
		return store.childrenAttr(parentKey, kind, attrName, attrValue);
	},
	put(parentKey, kind, rawEntities) {
		return store.put(parentKey, kind, rawEntities);
	},
	delete(parentKey, kind, rawEntities) {
		return store.delete(parentKey, kind, rawEntities);
	},
	observe(parentKeyOrKind, eventType, callback) {
		return store.observe(parentKeyOrKind, eventType, callback);
	},
	unobserve(parentKeyOrKind, eventType, callback) {
		return store.unobserve(parentKeyOrKind, eventType, callback);
	},
	globals
};



// WEBPACK FOOTER //
// ./src/core/store.js